import React, {
    useContext,
    useEffect, useMemo, useState 
} from 'react';
import {
    Box, Typography, 
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';

export const HabitsSection = (): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        checkmark: {
            width: '80px',
            height: '80px',
            margin: '0 auto',
            transform: 'translate(0)',
            zIndex: 1,
            animation: `$moveCheckmark ${skipAnimation ? 0 : 0.5}s forwards 0s, $moveCheckmark2 ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '10px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        section: {
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            margin: 'auto 0',
            backgroundColor: '#C0C0C0',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
        },
        habitsBackground: {
            margin: 'auto 0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#C0C0C0',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `$changeColor ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $semigrow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        icon: {
            fill: '#C0C0C0',
        },
        goalsSection: {
            opacity: 0,
        },
        habitsSection: {
            opacity: 1,
            animation: `$moveSection ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        habitsIcon: {
            opacity: 0,
            fill: '#B05DF1',
            width: '50%',
            height: '50%',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        aboutYouSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        planSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        targetSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        subtitle: {
            textAlign: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: '#C0C0C0' 
            },
            '100%': {
                backgroundColor: '#B05DF1' 
            },
        },
        '@keyframes semigrow': {
            '0%': {
                width: '16px',
                height: '16px' 
            },
            '100%': {
                width: '24px',
                height: '24px' 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '80px',
                height: '80px' 
            },
        },
        '@keyframes moveSection': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(26px, 0)' 
            },
        },
        '@keyframes moveCheckmark': {
            '0%': {
                transform: 'translate(0)',
                width: '80px',
                height: '80px' 
            },
            '100%': {
                transform: 'translate(-52px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
        },
        '@keyframes moveCheckmark2': {
            '0%': {
                transform: 'translate(-52px, 98px)' 
            },
            '100%': {
                transform: 'translate(-55px, 98px)' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();
    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);
    }, []);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={onBackClick}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <svg
                            className={classes.checkmark}
                            width="82"
                            height="82"
                            viewBox="0.5 0.5 82 82"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="41"
                                cy="41"
                                r="40.5"
                                fill="#5C5C5C"
                                stroke="#5C5C5C"
                            />
                            <path
                                d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                stroke={theme.palette.white}
                                strokeWidth="7.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <Box className={classes.sections}>
                            <Box className={`${classes.section} ${classes.goalsSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.habitsSection}`}>
                                <Box className={classes.habitsBackground}>
                                    <svg
                                        className={`${classes.icon} ${classes.habitsIcon}`}
                                        width="40"
                                        height="41"
                                        viewBox="0 0 40 41"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_2181_131027)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M6.90918 0.833336C6.08075 0.833336 5.40918 1.50491 5.40918 2.33334V12.1515C5.40918 19.6737 11.1701 25.9089 18.5 26.6654V36.8333H11.8184C10.9899 36.8333 10.3184 37.5049 10.3184 38.3333C10.3184 39.1618 10.9899 39.8333 11.8184 39.8333H28.182C29.0104 39.8333 29.682 39.1618 29.682 38.3333C29.682 37.5049 29.0104 36.8333 28.182 36.8333H21.5V26.6654C28.83 25.909 34.591 19.6737 34.591 12.1515V2.33334C34.591 1.50491 33.9194 0.833336 33.091 0.833336H6.90918ZM8.40918 10.6515V3.83334H31.591V10.6515H8.40918ZM8.50666 13.6515C9.24879 19.3222 14.1365 23.7424 20.0001 23.7424C25.8636 23.7424 30.7514 19.3222 31.4935 13.6515H8.50666Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2181_131027">
                                                <rect
                                                    width="40"
                                                    height="40"
                                                    fill={theme.palette.white}
                                                    transform="translate(0 0.333336)"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.aboutYouSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.planSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.targetSection}`}>
                                <Box className={classes.background} />
                            </Box>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.title}
                        >
                            {'Your Habits'}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.subtitle}
                        >
                            {'Now that you\'ve shared your goals, let\'s explore your current habits, which we\'ll use to personalize your Sunnyside plan.'}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default HabitsSection;
